import { api } from "shared/config/api/api";
import type {
  User,
  UserDTO,
  UserWallet,
  DepositsHistory,
  WithdrawalsHistory,
} from "../model/types";
import type { WithdrawalRequest, WithdrawalResponse } from "entities/wallet";

export const authApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getUser: builder.query<User, void>({
      query: () => "/users/me",
      providesTags: ["UserProfile"],
    }),
    getUserData: builder.query<UserDTO, void>({
      query: () => "/users/profile",
      providesTags: ["UserData"],
    }),
    updateUserNames: builder.mutation<
      UserDTO,
      { first_name: string; last_name: string }
    >({
      query: (names) => ({
        url: "/users/profile",
        method: "PATCH",
        body: names,
      }),
      invalidatesTags: ["UserData"],
    }),
    getUserWallets: builder.query<{ wallets: UserWallet[] }, void>({
      query: () => "/users/wallets",
      providesTags: ["UserWallets"],
    }),
    createWithdrawalRequest: builder.mutation<
      WithdrawalResponse,
      WithdrawalRequest
    >({
      query: (withdrawalRequest) => ({
        url: "/users/withdrawals",
        method: "POST",
        body: withdrawalRequest,
      }),
    }),
    getDepositsHistory: builder.query<
      DepositsHistory,
      { date_from: string; date_to: string } | void
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();

        if (params?.date_from)
          searchParams.append("date_from", params.date_from);
        if (params?.date_to) searchParams.append("date_to", params.date_to);

        const queryString = searchParams.toString();
        return {
          url: `/users/deposits${queryString ? `?${queryString}` : ""}`,
        };
      },
    }),
    getWithdrawalsHistory: builder.query<
      WithdrawalsHistory,
      { date_from: string; date_to: string } | void
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();

        if (params?.date_from)
          searchParams.append("date_from", params.date_from);
        if (params?.date_to) searchParams.append("date_to", params.date_to);

        const queryString = searchParams.toString();
        return {
          url: `/users/withdrawals${queryString ? `?${queryString}` : ""}`,
        };
      },
    }),
  }),
});

export const {
  useGetUserQuery,
  useGetUserDataQuery,
  useLazyGetUserQuery,
  useUpdateUserNamesMutation,
  useGetUserWalletsQuery,
  useCreateWithdrawalRequestMutation,
  useGetDepositsHistoryQuery,
  useGetWithdrawalsHistoryQuery,
} = authApi;
